import React, { useEffect, useRef, useState } from 'react';
import { Modal, Segmented, Button, Space, Typography } from 'antd';
import { ACCENTS, WOODS, useAccent, previewAccent, setAccent, styleOf, lastPick } from './HarborAccents';
import HarborTheme from './HarborPageTheme';
import api from '../../api';
import UserAvatar, { loadAvatars } from '../UserAvatar';
import { syncMyAvatar } from '../../utils/avatarSync';

// The profile page: opens over the current page from the profile knob.
// Picks show on the whole screen straight away; Apply keeps them, and closing
// without Apply puts back what was there when the page opened.
//
// To add a section, add an entry to SECTIONS. Each one gets:
//   draft   -- the settings as currently shown ({ style, dark, accent, ... })
//   change  -- change(patch) shows a patch at once (e.g. { accent: 'blue' })
//   user    -- who is signed in
// To add a setting, include it in the draft, preview it in change() and keep or
// restore it in apply() / cancel().

// Theme: a Style, then Dark or Light, then that style's colours.
//   Classic -- the default look (its own header); one set of colours. Layout owns this page so
//              it stays open while Classic replaces the Harbor bar behind it.
//   Yacht   -- the Harbor bar in metal with lit lips, in six colours (ACCENTS)
//   Wood    -- the Harbor bar as a plank of Dock, Cedar, Teak or Mahogany (WOODS)
//   Northern Lights -- a night sky over pines; night only, so no Mode, and one set of colours
const STYLES = [
  { key: 'classic', label: 'Classic' },
  { key: 'yacht', label: 'Yacht' },
  { key: 'wood', label: 'Wood' },
  { key: 'aurora', label: 'Northern Lights' },
];

function ThemeSection({ draft, change }) {
  const colours = draft.style === 'wood' ? WOODS : ACCENTS;
  return (
    <div className="hb-prof-section">
      <div className="hb-prof-field">
        <div className="hb-prof-label">Style</div>
        <div className="hb-prof-styles" role="radiogroup" aria-label="Style">
          {STYLES.map((s) => (
            <button
              key={s.key}
              type="button"
              role="radio"
              aria-checked={draft.style === s.key}
              className={`hb-prof-sty${draft.style === s.key ? ' hb-on' : ''}`}
              onClick={() => change({ style: s.key })}
            >
              <span className={`hb-prof-sty-icon hb-sty-${s.key}`}><i /></span>
              <span>{s.label}</span>
            </button>
          ))}
        </div>
      </div>
      {draft.style !== 'aurora' && (
        <div className="hb-prof-field">
          <div className="hb-prof-label">Mode</div>
          <Segmented
            className="hb-prof-mode"
            value={draft.dark ? 'dark' : 'light'}
            onChange={(v) => change({ dark: v === 'dark' })}
            options={[{ label: 'Dark', value: 'dark' }, { label: 'Light', value: 'light' }]}
          />
        </div>
      )}
      {draft.style !== 'classic' && draft.style !== 'aurora' && (
        <div className="hb-prof-field">
          <div className="hb-prof-label">{draft.style === 'wood' ? 'Wood' : 'Color'}</div>
          <div className="hb-prof-swatches" role="radiogroup" aria-label={draft.style === 'wood' ? 'Wood' : 'Color'}>
            {colours.map((a) => (
              <button
                key={a.key}
                type="button"
                role="radio"
                aria-checked={draft.accent === a.key}
                className={`hb-prof-swatch${draft.accent === a.key ? ' hb-on' : ''}`}
                onClick={() => change({ accent: a.key })}
              >
                <span className="hb-prof-chip" style={{ background: a.swatch }} />
                <span>{a.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const SECTIONS = [
  { key: 'theme', label: 'Theme', render: (props) => <ThemeSection {...props} /> },
  { key: 'photo', label: 'Photo', render: (props) => <PhotoSection {...props} /> },
];

// A square crop from the middle of the picked image, shrunk to 128 x 128.
function squareJpeg(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const src = URL.createObjectURL(file);
    img.onload = () => {
      const side = Math.min(img.width, img.height);
      const canvas = document.createElement('canvas');
      canvas.width = 128;
      canvas.height = 128;
      canvas.getContext('2d').drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, 128, 128);
      URL.revokeObjectURL(src);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not read that picture'))), 'image/jpeg', 0.9);
    };
    img.onerror = () => { URL.revokeObjectURL(src); reject(new Error('Could not read that picture')); };
    img.src = src;
  });
}

// Your picture next to your name around Harbor: from your Google or Microsoft
// 365 sign-in, or one you choose here. Saved straight away, not on Apply.
function PhotoSection({ user }) {
  const input = useRef(null);
  const [source, setSource] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const refresh = () => api.get('/api/me/avatar').then((r) => setSource(r.data.source)).catch(() => setSource(null));
  useEffect(() => { refresh(); }, []);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('source', 'upload');
      body.append('file', await squareJpeg(file), 'photo.jpg');
      await api.put('/api/me/avatar', body);
      await loadAvatars(true);
      await refresh();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not save that picture');
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.delete('/api/me/avatar');
      await syncMyAvatar({ force: true });   // back to the sign-in photo, if there is one
      await loadAvatars(true);
      await refresh();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove it');
    } finally {
      setBusy(false);
    }
  };

  const from = { upload: 'A picture you chose', google: 'From your Google sign-in', m365: 'From your Microsoft 365 account' }[source];
  return (
    <div className="hb-prof-section">
      <div className="hb-prof-field">
        <Space size={16} align="center">
          <UserAvatar userId={user?.ID} name={user?.DisplayName} size={72} />
          <div>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              {source === undefined ? '' : from || 'Your initials, until there is a picture'}
            </Typography.Text>
            <Space wrap>
              <Button onClick={() => input.current?.click()} loading={busy}>
                {source === 'upload' ? 'Change photo' : 'Upload photo'}
              </Button>
              {source === 'upload' && <Button onClick={remove} disabled={busy}>Remove</Button>}
            </Space>
            <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={pick} />
          </div>
        </Space>
        {error && <Typography.Text type="danger" style={{ display: 'block', marginTop: 8 }}>{error}</Typography.Text>}
        <Typography.Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
          Shown next to your name around Harbor. Saved straight away. Removing your own picture goes back to your sign-in photo.
        </Typography.Text>
      </div>
    </div>
  );
}

export default function ProfileModal({ open, onClose, user, dark, setDark, navStyle, setNavStyle }) {
  const accent = useAccent();
  const saved = useRef(null); // the settings when the page opened, to put back on close
  const picks = useRef({});   // each style's colour as last picked here, so switching style brings it back
  const [style, setStyle] = useState(navStyle === 'classic' ? 'classic' : styleOf(accent));
  const [section, setSection] = useState(SECTIONS[0].key);

  useEffect(() => {
    if (open) {
      saved.current = { dark, accent, navStyle };
      picks.current = { yacht: lastPick('yacht'), wood: lastPick('wood'), aurora: 'aurora' };
      setStyle(navStyle === 'classic' ? 'classic' : styleOf(accent));
      setSection(SECTIONS[0].key);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // This page keeps the Harbor colours while Classic shows behind it; HarborNav sets these
  // otherwise. Harmless to the classic look, which no hb- rule touches.
  useEffect(() => {
    if (!open || navStyle !== 'classic') return undefined;
    const el = document.documentElement;
    el.dataset.hbAccent = accent;
    el.dataset.hbStyle = styleOf(accent);
    return () => {
      if (!el.classList.contains('hb-on')) { delete el.dataset.hbAccent; delete el.dataset.hbStyle; }
    };
  }, [open, navStyle, accent]);

  const draft = { style, dark, accent };
  const change = (patch) => {
    if ('dark' in patch && patch.dark !== dark) setDark(patch.dark);
    if ('style' in patch) {
      setStyle(patch.style);
      setNavStyle(patch.style === 'classic' ? 'classic' : 'harbor');
      if (patch.style !== 'classic') previewAccent(picks.current[patch.style]);
      if (patch.style === 'aurora' && !dark) setDark(true);   // night only
    }
    if ('accent' in patch) {
      picks.current[styleOf(patch.accent)] = patch.accent;
      previewAccent(patch.accent);
    }
  };
  const apply = () => {
    const s = saved.current;
    saved.current = null;
    // Classic: the Harbor colour stays as it was, for coming back
    if (style === 'classic') { if (s) previewAccent(s.accent); } else setAccent(accent);
    onClose();
  };
  const cancel = () => {
    const s = saved.current;
    if (s) {
      if (s.dark !== dark) setDark(s.dark);
      if (s.navStyle !== navStyle) setNavStyle(s.navStyle);
      previewAccent(s.accent);
    }
    onClose();
  };
  const current = SECTIONS.find((s) => s.key === section) || SECTIONS[0];

  return (
    <HarborTheme dark={dark}>
      <Modal
        open={open}
        onCancel={cancel}
        title={<span className="hb-prof-title">Profile{user?.DisplayName ? <small>{user.DisplayName}</small> : null}</span>}
        width={640}
        rootClassName="hb-profile hb-root"
        destroyOnClose
        footer={<Button type="primary" onClick={apply}>Apply</Button>}
      >
        <div className="hb-prof-body">
          <nav className="hb-prof-side" aria-label="Profile sections">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                type="button"
                className={`hb-prof-tab${s.key === current.key ? ' hb-on' : ''}`}
                aria-current={s.key === current.key ? 'page' : undefined}
                onClick={() => setSection(s.key)}
              >
                {s.label}
              </button>
            ))}
          </nav>
          <div className="hb-prof-main">
            <h3 className="hb-prof-h">{current.label}</h3>
            {current.render({ draft, change, user })}
          </div>
        </div>
      </Modal>
    </HarborTheme>
  );
}
